/**
 * Exact-slot list reorder — the algorithm behind chapter, scene, and act drag.
 *
 * Dropping onto an item means "take this item's original index", not "insert
 * before/after with an invisible split". Nothing `toKey` means append at the end.
 *
 * The target index MUST be read from the *original* array. Looking it up after
 * removing `fromKey` shifts every later index down by one, which silently lands
 * one slot early on any forward drag (a real regression: dragging item 1 onto
 * item 4 landed it in 3rd position instead of 4th).
 */
import { isJust, type Maybe, matchMaybe } from "./maybe";

export function reorderExactSlotByKey<T>(
  items: T[],
  keyOf: (item: T) => string,
  fromKey: string,
  toKey: Maybe<string>,
): T[] {
  const fromIdx = items.findIndex((item) => keyOf(item) === fromKey);
  if (fromIdx < 0) return items;
  const toIdx = matchMaybe(toKey, {
    nothing: () => items.length,
    just: (key) => items.findIndex((item) => keyOf(item) === key),
  });
  if (isJust(toKey) && toIdx < 0) return items;
  const next = [...items];
  const removed = next.splice(fromIdx, 1);
  const moved = removed[0];
  if (typeof moved === "undefined") return items;
  next.splice(Math.max(0, Math.min(toIdx, next.length)), 0, moved);
  return next;
}

/** Chapter-list twin — same algorithm, keyed by `folder`. */
export function reorderChaptersExactSlot<T extends { folder: string }>(
  chapters: T[],
  fromFolder: string,
  toFolder: Maybe<string>,
): T[] {
  return reorderExactSlotByKey(chapters, (c) => c.folder, fromFolder, toFolder);
}

/** Scene-list twin — same algorithm, keyed by `file`. */
export function reorderScenesExactSlot<T extends { file: string }>(
  scenes: T[],
  fromFile: string,
  toFile: Maybe<string>,
): T[] {
  return reorderExactSlotByKey(scenes, (s) => s.file, fromFile, toFile);
}
